import { FileDownloadOutlined } from "@mui/icons-material";
import { Button, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";

import { useNotification } from "../../contexts/NotificationContext";

export interface ExportOption {
  key: string;
  label: string;
  // Writes the file; may take a while and may fail
  run: () => void | Promise<void>;
}

interface ExportMenuProps {
  options: ExportOption[];
  // What the trigger reads
  label?: string;
  disabled?: boolean;
}

// The outlined button that opens a menu of the ways to export. While an
// option runs the button reads "Exporting…"; a failure is reported, not
// thrown.
const ExportMenu: React.FC<ExportMenuProps> = ({
  options,
  label = "Export",
  disabled = false,
}) => {
  const { showNotification } = useNotification();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [running, setRunning] = useState(false);

  const pick = async (option: ExportOption) => {
    setAnchor(null);
    setRunning(true);
    try {
      await option.run();
    } catch (exc) {
      console.error(exc);
      showNotification(
        exc instanceof Error && exc.message ? exc.message : "The export failed",
        "error",
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadOutlined />}
        aria-haspopup="menu"
        disabled={disabled || running}
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          setAnchor(event.currentTarget)
        }
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        {running ? "Exporting…" : label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
      >
        {options.map((option) => (
          <MenuItem key={option.key} dense onClick={() => pick(option)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ExportMenu;
